import { useEffect, useRef } from 'react';

// 터치 지점에서 위로 올라가며 해당 방향으로 더 스크롤 가능한 조상 요소 탐색 //
function findScrollableAncestor(target, deltaY, deltaX, isMovingVertically) {
  let el = target;

  while (el && el.nodeType === 1 && el !== document.body) {
    const style = window.getComputedStyle(el);

    if (isMovingVertically) {
      const overflowY = style.overflowY;
      const isScrollableV = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;

      if (isScrollableV) {
        const isAtTop = el.scrollTop <= 0;
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

        if (!(isAtTop && deltaY > 0) && !(isAtBottom && deltaY < 0)) return el;
      }
    }
    else {
      const overflowX = style.overflowX;
      const isScrollableH = (overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth;

      if (isScrollableH) {
        const isAtLeft = el.scrollLeft <= 0;
        const isAtRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

        if (!(isAtLeft && deltaX > 0) && !(isAtRight && deltaX < 0)) return el;
      }
    }

    el = el.parentElement;
  }

  return null;
}

// 모달 스크롤 제어 훅 (iOS Safari/Chrome 대응 포함, 수평/수직 스크롤 지원) //
export default function useModalScroll(options = {}) {
  const {
    includeViewportSync = true,
    viewportTopCssVar = '--modal-viewport-top',
    viewportHeightCssVar = '--modal-viewport-height',
    viewportTopSource = 'scrollY',
    viewportLeftCssVar = '--modal-viewport-left',
    viewportWidthCssVar = '--modal-viewport-width',
    viewportLeftSource = 'scrollX'
  } = options;

  const topLevelRef = useRef();
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);

  useEffect(() => {
    const topLevelNode = topLevelRef.current;
    document.body.style.overflow = 'hidden';

    // iOS 뷰포트 바운스 방지: 모달 스크롤 경계 도달 시 touchmove 차단 //
    function handleTouchStart(e) {
      touchStartY.current = e.touches[0].clientY;
      touchStartX.current = e.touches[0].clientX;
    }

    function handleTouchMove(e) {
      const el = topLevelRef.current;
      if (!el) return;

      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const deltaY = currentY - touchStartY.current;
      const deltaX = currentX - touchStartX.current;

      // 주된 이동 방향 판단 //
      const isMovingVertically = Math.abs(deltaY) >= Math.abs(deltaX);

      // 터치 지점 기준으로 해당 방향으로 더 스크롤 가능한 요소(드롭다운 등 중첩 영역 포함) 탐색 //
      const scrollableTarget = findScrollableAncestor(e.target, deltaY, deltaX, isMovingVertically);

      // 스크롤 소비 가능한 요소가 있으면 브라우저 기본 스크롤 허용 //
      if (scrollableTarget) return;

      // 소비할 요소가 없으면 바운스/오버스크롤 차단 //
      e.preventDefault();
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    // 모바일 브라우저 주소창 변동 시 viewport top/height CSS 변수 동기화 //
    function syncViewport() {
      if (!includeViewportSync) return;
      if (!topLevelRef.current) return;

      const vv = window.visualViewport;

      // ----- 세로(top/height) -----
      let viewportTop = 0;

      if (viewportTopSource === 'offsetTop') {
        viewportTop = vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
      }
      else if (viewportTopSource === 'innerMinusVV') {
        viewportTop = vv ? Math.max(0, Math.round(window.innerHeight - vv.height)) : 0;
      }
      else {
        viewportTop = Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0));
      }

      const viewportHeight = Math.max(0, Math.round(window.innerHeight));

      // ----- 가로(left/width) -----
      let viewportLeft = 0;

      if (viewportLeftSource === 'offsetLeft') {
        viewportLeft = vv ? Math.round(vv.offsetLeft || 0) : 0;
      }
      else {
        viewportLeft = Math.round(window.scrollX || window.pageXOffset || 0);
      }

      const viewportWidth = Math.max(
        0,
        Math.round((vv && vv.width) ? vv.width : window.innerWidth)
      );

      const el = topLevelRef.current;
      
      el.style.setProperty(viewportTopCssVar, `${viewportTop}px`);
      el.style.setProperty(viewportHeightCssVar, `${viewportHeight}px`);

      el.style.setProperty(viewportLeftCssVar, `${viewportLeft}px`);
      el.style.setProperty(viewportWidthCssVar, `${viewportWidth}px`);
    }

    syncViewport();

    if (includeViewportSync) {
      window.addEventListener('resize', syncViewport);
      window.addEventListener('orientationchange', syncViewport);

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', syncViewport);
        window.visualViewport.addEventListener('scroll', syncViewport);
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);

      if (includeViewportSync) {
        if (topLevelNode) {
          topLevelNode.style.removeProperty(viewportTopCssVar);
          topLevelNode.style.removeProperty(viewportHeightCssVar);
          topLevelNode.style.removeProperty(viewportLeftCssVar);
          topLevelNode.style.removeProperty(viewportWidthCssVar);
        }

        window.removeEventListener('resize', syncViewport);
        window.removeEventListener('orientationchange', syncViewport);

        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', syncViewport);
          window.visualViewport.removeEventListener('scroll', syncViewport);
        }
      }
    };
  }, [
    includeViewportSync,
    viewportHeightCssVar,
    viewportTopCssVar,
    viewportTopSource,
    viewportLeftCssVar,
    viewportWidthCssVar,
    viewportLeftSource
  ]);

  return topLevelRef;
}
